import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon3";
import { DateTime } from "luxon";
import { RootState } from "typedefs";
import { CalendarPreferences } from "app/models/CalendarPreferences";
import { setSelectedEvent, fetchEvents, clearEvents } from "app/actions/events";
import i18n from "app/i18n";

import Event from "../../Event";
import { startOfCalendarWeek } from "../../utils";
import renderDayViewHeader from "./renderDayHeader";
import DayHeaderMobile from "../../DayHeaderMobile";
import styles from "./styles.module.scss";

interface DayViewProps {
  calendarRef: React.RefObject<any>;
  timeZone: string;
  preferences: CalendarPreferences;
  eventElRef: React.RefObject<any>;
  initialDate: DateTime;
  currentDay: DateTime;
  isMobile: boolean;
  businessHours: any;
  onIntervalChange: (start: DateTime, end: DateTime) => void;
  onSelect: (info: any) => void;
  onUnselect: () => void;
  unselectCancel: string;
  snapDuration: string;
  onDateChange: (date: DateTime) => void;
}

export default function DayView({
  calendarRef,
  timeZone,
  preferences,
  eventElRef,
  initialDate,
  currentDay,
  isMobile,
  businessHours,
  onIntervalChange,
  onSelect,
  onUnselect,
  unselectCancel,
  snapDuration,
  onDateChange,
}: DayViewProps) {
  const dispatch = useDispatch();
  const events = useSelector((state: RootState) => state.events);

  useEffect(() => {
    dispatch(clearEvents());
  }, []);

  const renderEvent = useCallback(
    (info: any) => <Event {...info} timeZone={timeZone} />,
    [timeZone],
  );

  const renderDayHeader = useCallback(
    (info: any) => {
      const tz = info.view.dateEnv.timeZone;
      if (isMobile) {
        const startOfWeek = startOfCalendarWeek(
          DateTime.fromJSDate(info.view.currentStart).setZone(tz),
          preferences.firstDay,
        );
        return (
          <DayHeaderMobile
            startOfWeek={startOfWeek.toJSDate()}
            events={events.data as any[]}
            view={info.view}
            onDateChange={onDateChange}
            selectedDate={currentDay}
          />
        );
      }
      return renderDayViewHeader(info, {
        events: events.data as any[],
        currentDay,
        onDateChange,
        firstDay: preferences.firstDay,
      });
    },
    [isMobile, events.data, currentDay, onDateChange, preferences.firstDay],
  );

  const handleEventClick = useCallback(
    (info: any) => {
      (eventElRef as React.MutableRefObject<any>).current = info.el;
      dispatch(setSelectedEvent(parseInt(info.event.id, 10)));
    },
    [dispatch, eventElRef],
  );

  const [displayedDate, setDisplayedDate] = useState<DateTime>(initialDate);

  const handleDatesSet = useCallback(
    (dateInfo: any) => {
      const currentDate = isMobile
        ? DateTime.fromJSDate(dateInfo.view.currentStart).setZone(timeZone)
        : DateTime.fromJSDate(dateInfo.start).setZone(timeZone);

      setDisplayedDate(currentDate);
    },
    [timeZone, isMobile],
  );

  // The header week strip needs the whole week's events for its dots, so we
  // fetch by week — but only when the displayed day crosses into another week
  // (or the first-day-of-week preference arrives/changes).
  const weekStartMillis = displayedDate
    ? startOfCalendarWeek(displayedDate, preferences.firstDay).toMillis()
    : null;

  useEffect(() => {
    if (weekStartMillis === null) {
      return;
    }
    const weekStart = DateTime.fromMillis(weekStartMillis).setZone(timeZone);
    const weekEnd = weekStart.plus({ days: 7 });

    onIntervalChange(weekStart, weekEnd);
    dispatch(
      fetchEvents({
        start: weekStart.toISO(),
        end: weekEnd.toISO(),
      }) as any,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartMillis]);

  return (
    <div className={styles.container}>
    <FullCalendar
      headerToolbar={false}
      ref={calendarRef}
      events={events.data as any[]}
      locale={i18n.language}
      allDaySlot={false}
      plugins={[timeGridPlugin, luxonPlugin, interactionPlugin]}
      initialView={isMobile ? "timeGridNarrow" : "timeGridDay"}
      initialDate={initialDate?.toJSDate()}
      timeZone={timeZone}
      height="100%"
      businessHours={businessHours}
      slotDuration="01:00:00"
      scrollTime="05:45:00"
      nowIndicator={true}
      nowIndicatorClassNames="now-indicator-line now-indicator-container"
      eventMinHeight={20}
      eventBackgroundColor="#039BE5"
      longPressDelay={600}
      selectMirror={true}
      unselectAuto={true}
      expandRows={true}
      selectable={true}
      unselectCancel={unselectCancel}
      select={onSelect}
      unselect={onUnselect}
      snapDuration={snapDuration}
      views={{
        timeGridNarrow: {
          type: "timeGrid",
          duration: { days: 1 },
        },
      }}
      dayHeaderFormat={{ day: "numeric" as const, weekday: "short" as const }}
      slotLabelFormat={{
        hour: "numeric" as const,
        minute: "2-digit" as const,
        omitZeroMinute: true,
        hour12: preferences.timeFormat === "12h",
      }}
      eventTimeFormat={{
        hour: "numeric" as const,
        minute: "2-digit" as const,
        omitZeroMinute: true,
        hour12: preferences.timeFormat === "12h",
      }}
      eventContent={renderEvent}
      dayHeaderContent={renderDayHeader}
      eventClick={handleEventClick}
      datesSet={handleDatesSet}
    />
    </div>
  );
}
